import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '../stores/useAppStore'
import { workOrderService, type WorkOrder, type TechnicianDayDestination } from '../services/workOrderService'
import { offlineWorkOrdersListService } from '../services/offlineWorkOrdersListService'
import { workOrderPrefetchService } from '../services/workOrderPrefetchService'
import { 
  ClockIcon, 
  MapPinIcon, 
  UserIcon, 
  ArrowPathIcon,
  KeyIcon,
  TagIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  Bars3Icon,
  StopIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog'
import { RunningOrderModal } from '../components/RunningOrderModal'
import { DayPlanningModal } from '../components/DayPlanningModal'

interface WorkOrderWithComments extends WorkOrder {
  editingComments?: boolean
  tempComments?: string
  clientNotes?: string | null
  editingTechOrderNo?: boolean
  tempTechOrderNo?: number | null
  editingRequestedTime?: boolean
  tempRequestedTime?: string | null
  keyPickup?: boolean
}
import WorkOrderMap from '../components/WorkOrderMap'

type CombinedItem =
  | { kind: 'WorkOrder'; key: string; order: number | null; workOrder: WorkOrderWithComments }
  | { kind: 'Destination'; key: string; order: number | null; destination: TechnicianDayDestination }

// Helper function to format time as hour:minutes
const formatTime = (timeString: string | null | undefined): string | null => {
  if (!timeString) return null
  
  try {
    // Handle different time formats
    if (timeString.includes('T')) {
      // ISO format like "2024-01-15T14:30:00"
      const date = new Date(timeString)
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false 
      })
    } else if (timeString.includes(':')) {
      // Already in time format like "14:30" or "14:30:00"
      const parts = timeString.split(':')
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
      }
    }
    
    // If it's just a time string, return as is
    return timeString
  } catch (error) {
    console.warn('Error formatting time:', timeString, error)
    return timeString
  }
}

const WorkOrdersList: React.FC = () => {
  const { user, selectedDate, setSelectedDate } = useAppStore()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>('booked') // Default to booked jobs
  const [priorityFilter] = useState<string>('all')
  const [workOrdersState, setWorkOrdersState] = useState<WorkOrderWithComments[]>([])
  const [showClientNotesModal, setShowClientNotesModal] = useState(false)
  const [selectedClientNotes, setSelectedClientNotes] = useState<string>('')
  const [showMap, setShowMap] = useState(false)
  const [showRunningOrderModal, setShowRunningOrderModal] = useState(false)
  const [showDayPlanningModal, setShowDayPlanningModal] = useState(false)
  const [isPrefetching, setIsPrefetching] = useState(false)
  const [lastPrefetchTime, setLastPrefetchTime] = useState<number | null>(null)
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [draggedOverItem, setDraggedOverItem] = useState<string | null>(null)
  const [dropPosition, setDropPosition] = useState<'above' | 'below' | null>(null)
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [isUpdatingEta, setIsUpdatingEta] = useState(false)
  const [dayDestinations, setDayDestinations] = useState<TechnicianDayDestination[]>([])
  const [showWorkOrdersOnly, setShowWorkOrdersOnly] = useState(false)
  const autoScrollRef = useRef<number | null>(null)
  const dragOverHandlerRef = useRef<((e: DragEvent) => void) | null>(null)

  const technicianId = user?.technicianId ?? (user?.id && !isNaN(Number(user.id)) ? Number(user.id) : null)
  const isValidTechnician = typeof technicianId === 'number' && technicianId > 0

  // Set selected date to today only on initial load (when no date is selected)
  // This preserves the selected date when navigating back from a work order
  useEffect(() => {
    if (!selectedDate) {
      const today = new Date().toISOString().split('T')[0]
      setSelectedDate(today)
    }
  }, [user?.id])

  useEffect(() => {
    if (isValidTechnician) {
      setIsPrefetching(true)
      workOrderPrefetchService.prefetchTodayWorkOrders(technicianId)
        .catch(error => console.error('Prefetch error:', error))
        .finally(() => {
          setIsPrefetching(false)
          setLastPrefetchTime(Date.now())
        })

      workOrderPrefetchService.startAutoRefresh(technicianId)
    }

    return () => {
      workOrderPrefetchService.cancelPrefetch()
      workOrderPrefetchService.stopAllAutoRefresh()
    }
  }, [technicianId, selectedDate, isValidTechnician])

  // Load technician day destinations when tech/date changes
  useEffect(() => {
    if (isValidTechnician && technicianId && selectedDate) {
      offlineWorkOrdersListService
        .loadDayDestinations(technicianId, selectedDate)
        .then(setDayDestinations)
        .catch(() => {})
    }
  }, [isValidTechnician, technicianId, selectedDate])

  // useEffect(() => {
  //   if (isValidTechnician) {
  //     const interval = setInterval(() => {
  //       const lastTime = workOrderPrefetchService.getLastPrefetchTime(technicianId, selectedDate)
  //       setLastPrefetchTime(lastTime)
  //     }, 60000) 

  //     return () => clearInterval(interval)
  //   }
  // }, [technicianId, selectedDate, isValidTechnician])
  
  const getTimeUntilNextRefresh = (): string => {
    if (!lastPrefetchTime || !isValidTechnician) return ''
    
    const timeUntilNext = workOrderPrefetchService.getTimeUntilNextPrefetch(technicianId, selectedDate)
    if (timeUntilNext === 0) return 'refreshing soon'
    
    const minutes = Math.ceil(timeUntilNext / (1000 * 60))
    return `next refresh in ${minutes}m`
  }

  const {
    data: workOrdersResponse,
    isLoading: loadingWorkOrders,
    error: workOrdersError,
    refetch: refetchWorkOrders
  } = useQuery({
    queryKey: ['filtered-workorders-offline', technicianId, selectedDate, statusFilter],
    queryFn: () => {
      if (!isValidTechnician) {
        throw new Error('Invalid technician ID')
      }
      return offlineWorkOrdersListService.loadWorkOrdersList(technicianId, selectedDate, statusFilter)
    },
    enabled: !!isValidTechnician,
    staleTime: 0, // Data is immediately stale for aggressive refresh
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
  })

  const workOrders = workOrdersResponse?.data || []
  const workOrdersListState = workOrdersResponse?.state

  useEffect(() => {
    if (workOrders && workOrders.length > 0) {
      // Sort work orders by techOrderNo, with null/undefined values at the end
      const sortedWorkOrders = [...workOrders].sort((a, b) => {
        const aVal = a.techOrderNo ?? null
        const bVal = b.techOrderNo ?? null
        if (aVal === null && bVal === null) return 0
        if (aVal === null) return 1
        if (bVal === null) return -1
        return aVal - bVal
      })

      // Preserve editing states from current state
      setWorkOrdersState(prev => {
        return sortedWorkOrders.map(wo => {
          const existing = prev.find(p => p.workOrderID === wo.workOrderID)
          return {
            ...wo,
            editingComments: existing?.editingComments || false,
            tempComments: existing?.editingComments ? existing.tempComments : (wo.workOrderNotes || ''),
            editingTechOrderNo: existing?.editingTechOrderNo || false,
            tempTechOrderNo: existing?.editingTechOrderNo ? existing.tempTechOrderNo : wo.techOrderNo,
            editingRequestedTime: existing?.editingRequestedTime || false,
            tempRequestedTime: existing?.editingRequestedTime ? existing.tempRequestedTime : wo.requestedTime,
            keyPickup: wo.keyPickup
          }
        })
      })
    } else {
      // Clear state when there are no work orders for the selected date
      setWorkOrdersState([])
    }
  }, [workOrders])

  // Periodic refresh for work orders list - only for today's date
  useEffect(() => {
    const refreshWorkOrdersList = async () => {
      if (isValidTechnician && technicianId) {
        const today = new Date().toISOString().split('T')[0]
        const isViewingToday = selectedDate === today

        // Only refresh when viewing today's date
        if (isViewingToday) {
          try {
            // Fetch fresh data from API and update cache
            await workOrderPrefetchService.prefetchTodayWorkOrders(technicianId)

            // Refetch from updated cache
            await refetchWorkOrders()
          } catch (error) {
            console.error('Error refreshing work orders:', error)
          }
        }

        // Always refresh day destinations if we have valid data
        if (selectedDate) {
          try {
            const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
            setDayDestinations(destinations)
          } catch (error) {
            console.error('Error refreshing destinations:', error)
          }
        }
      }
    }

    // Refresh every 15 seconds for faster updates
    const interval = setInterval(refreshWorkOrdersList, 15000)

    return () => clearInterval(interval)
  }, [isValidTechnician, refetchWorkOrders, selectedDate, technicianId])

  // Page visibility refresh - refresh when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isValidTechnician && technicianId) {
        try {
          // Fetch fresh data from API and update cache
          await workOrderPrefetchService.prefetchTodayWorkOrders(technicianId)

          // Refetch work orders from updated cache
          await refetchWorkOrders()

          // Refresh day destinations
          if (selectedDate) {
            const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
            setDayDestinations(destinations)
          }
        } catch (error) {
          console.error('Error refreshing on visibility change:', error)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isValidTechnician, refetchWorkOrders, technicianId, selectedDate])

  // Refetch when component mounts or when navigating back to this page
  useEffect(() => {
    const refreshOnMount = async () => {
      if (isValidTechnician && technicianId) {
        try {
          // Fetch fresh data from API and update cache
          await workOrderPrefetchService.prefetchTodayWorkOrders(technicianId)

          // Refetch from updated cache
          await refetchWorkOrders()

          // Refresh day destinations
          if (selectedDate) {
            const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
            setDayDestinations(destinations)
          }
        } catch (error) {
          console.error('Error refreshing on mount:', error)
        }
      }
    }

    refreshOnMount()
  }, [isValidTechnician, refetchWorkOrders, technicianId, selectedDate])

  const filteredWorkOrders = workOrdersState.filter(wo => {
    const priorityMatch = priorityFilter === 'all' || wo.priority?.toLowerCase() === priorityFilter.toLowerCase()
    return priorityMatch
  })

  const combinedItems: CombinedItem[] = React.useMemo(() => {
    const workItems = filteredWorkOrders.map((wo) => ({
      kind: 'WorkOrder' as const,
      key: `W-${wo.workOrderID}`,
      order: wo.techOrderNo ?? null,
      workOrder: wo,
    }))
    
    // Only include destinations if not showing work orders only
    let destItems: CombinedItem[] = []
    if (!showWorkOrdersOnly) {
      destItems = dayDestinations
        .filter((dest) => {
          // Filter destinations based on status filter
          if (statusFilter === 'booked') {
            // Show only non-completed destinations
            return !dest.completed
          } else if (statusFilter === 'completed') {
            // Show only completed destinations
            return dest.completed
          } else if (statusFilter === 'cancelled' || statusFilter === 'unable to complete') {
            // Don't show destinations for cancelled/unable to complete filters
            return false
          }
          // For "all", show all destinations
          return true
        })
        .map((dest) => ({
          kind: 'Destination' as const,
          key: `D-${dest.technicianDayDestinationID}`,
          order: dest.orderSequence ?? null,
          destination: dest,
        }))
    }
    
    const all = [...workItems, ...destItems]
    all.sort((a, b) => {
      const aVal = a.order ?? null
      const bVal = b.order ?? null
      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1
      return aVal - bVal
    })
    return all
  }, [filteredWorkOrders, dayDestinations, showWorkOrdersOnly, statusFilter])

  const handleWorkOrderClick = (workOrderId: number) => {
    navigate(`/work-orders/${workOrderId}`)
  }

  const openGoogleMaps = (address: string) => {
    const encodedAddress = encodeURIComponent(address)
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
    window.open(googleMapsUrl, '_blank')
  }

  const startEditingComments = (workOrderId: number) => {
    setWorkOrdersState(prev => prev.map(wo => 
      wo.workOrderID === workOrderId 
        ? { ...wo, editingComments: true, tempComments: wo.workOrderNotes || '' }
        : wo
    ))
  }

  const saveComments = async (workOrderId: number, comments: string) => {
    try {
      await workOrderService.updateWorkOrderComment(workOrderId, comments.trim() || null)
      setWorkOrdersState(prev => prev.map(wo => 
        wo.workOrderID === workOrderId 
          ? { ...wo, editingComments: false, workOrderNotes: comments }
          : wo
      ))
    } catch (error) {
      console.error('Error updating work order comments:', error)
    }
  }

  const cancelEditingComments = (workOrderId: number) => {
    setWorkOrdersState(prev => prev.map(wo => 
      wo.workOrderID === workOrderId 
        ? { ...wo, editingComments: false, tempComments: wo.workOrderNotes || '' }
        : wo
    ))
  }

  const updateTempComments = (workOrderId: number, comments: string) => {
    setWorkOrdersState(prev => prev.map(wo => 
      wo.workOrderID === workOrderId 
        ? { ...wo, tempComments: comments }
        : wo
    ))
  }

  const showClientNotesDialog = (clientNotes: string) => {
    setSelectedClientNotes(clientNotes)
    setShowClientNotesModal(true)
  }

  const startEditingTechOrderNo = (workOrderId: number) => {
    setWorkOrdersState(prev => prev.map(wo => 
      wo.workOrderID === workOrderId 
        ? { ...wo, editingTechOrderNo: true, tempTechOrderNo: wo.techOrderNo }
        : wo
    ))
  }

  const saveTechOrderNo = async (workOrderId: number, techOrderNo: number | null) => {
    try {
      await workOrderService.updateTechOrderNo(workOrderId, techOrderNo)
      setWorkOrdersState(prev => {
        const updated = prev.map(wo => 
          wo.workOrderID === workOrderId 
            ? { ...wo, editingTechOrderNo: false, techOrderNo: techOrderNo }
            : wo
        )
        // Re-sort after updating
        return updated.sort((a, b) => {
          const aVal = a.techOrderNo ?? null
          const bVal = b.techOrderNo ?? null
          if (aVal === null && bVal === null) return 0
          if (aVal === null) return 1
          if (bVal === null) return -1
          return aVal - bVal
        })
      })
    } catch (error) {
      console.error('Error updating tech order number:', error)
    }
  }

  const cancelEditingTechOrderNo = (workOrderId: number) => {
    setWorkOrdersState(prev => prev.map(wo => 
      wo.workOrderID === workOrderId 
        ? { ...wo, editingTechOrderNo: false, tempTechOrderNo: wo.techOrderNo }
        : wo
    ))
  }

  const updateTempTechOrderNo = (workOrderId: number, techOrderNo: string) => {
    const numValue = techOrderNo === '' ? null : parseInt(techOrderNo)
    setWorkOrdersState(prev => prev.map(wo =>
      wo.workOrderID === workOrderId
        ? { ...wo, tempTechOrderNo: numValue }
        : wo
    ))
  }

  const startEditingRequestedTime = (workOrderId: number) => {
    setWorkOrdersState(prev => prev.map(wo =>
      wo.workOrderID === workOrderId
        ? { ...wo, editingRequestedTime: true, tempRequestedTime: wo.requestedTime || '' }
        : wo
    ))
  }

  const saveRequestedTime = async (workOrderId: number, requestedTime: string | null) => {
    try {
      // Use the dedicated update method for requested time
      await workOrderService.updateRequestedTime(workOrderId, requestedTime)

      setWorkOrdersState(prev => prev.map(wo =>
        wo.workOrderID === workOrderId
          ? { ...wo, editingRequestedTime: false, requestedTime: requestedTime }
          : wo
      ))
    } catch (error) {
      console.error('Error updating requested time:', error)
    }
  }

  const cancelEditingRequestedTime = (workOrderId: number) => {
    setWorkOrdersState(prev => prev.map(wo =>
      wo.workOrderID === workOrderId
        ? { ...wo, editingRequestedTime: false, tempRequestedTime: wo.requestedTime || '' }
        : wo
    ))
  }

  const updateTempRequestedTime = (workOrderId: number, requestedTime: string) => {
    setWorkOrdersState(prev => prev.map(wo =>
      wo.workOrderID === workOrderId
        ? { ...wo, tempRequestedTime: requestedTime }
        : wo
    ))
  }

  const autoScroll = (clientY: number) => {
    const threshold = 80
    const maxSpeed = 15
    const viewportHeight = window.innerHeight

    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current)
      autoScrollRef.current = null
    }

    let speed = 0
    if (clientY < threshold) {
      speed = -maxSpeed * (1 - clientY / threshold)
    } else if (clientY > viewportHeight - threshold) {
      speed = maxSpeed * (1 - (viewportHeight - clientY) / threshold)
    }

    if (speed !== 0) {
      window.scrollBy(0, speed)
      autoScrollRef.current = requestAnimationFrame(() => autoScroll(clientY))
    }
  }

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current)
      autoScrollRef.current = null
    }
  }

  const handleDragStart = (e: React.DragEvent, itemKey: string) => {
    setDraggedItem(itemKey)
    e.dataTransfer.effectAllowed = 'move'

    // Attach document-level dragover for auto-scroll at viewport edges
    const handler = (ev: DragEvent) => {
      autoScroll(ev.clientY)
    }
    dragOverHandlerRef.current = handler
    document.addEventListener('dragover', handler)
  }

  const handleDragOver = (e: React.DragEvent, itemKey: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDraggedOverItem(itemKey)

    // Calculate if cursor is in top or bottom half of row
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    setDropPosition(e.clientY < midY ? 'above' : 'below')
  }

  const handleDragLeave = () => {
    setDraggedOverItem(null)
    setDropPosition(null)
  }

  const handleDrop = async (e: React.DragEvent, targetKey: string) => {
    e.preventDefault()
    stopAutoScroll()
    if (dragOverHandlerRef.current) {
      document.removeEventListener('dragover', dragOverHandlerRef.current)
      dragOverHandlerRef.current = null
    }

    if (!draggedItem || draggedItem === targetKey) {
      setDraggedItem(null)
      setDraggedOverItem(null)
      setDropPosition(null)
      return
    }

    const draggedIndex = combinedItems.findIndex(ci => ci.key === draggedItem)
    const targetIndex = combinedItems.findIndex(ci => ci.key === targetKey)

    if (draggedIndex === -1 || targetIndex === -1) return

    // Create new array with reordered items
    const newCombined = [...combinedItems]
    const draggedEntry = newCombined[draggedIndex]
    newCombined.splice(draggedIndex, 1)
    newCombined.splice(targetIndex, 0, draggedEntry)

    try {
      if (isValidTechnician && technicianId && selectedDate) {
        const updates = newCombined.map((ci, index) => ({
          type: ci.kind === 'WorkOrder' ? 'WorkOrder' as const : 'Destination' as const,
          id: ci.kind === 'WorkOrder' ? ci.workOrder.workOrderID : ci.destination.technicianDayDestinationID,
          order: index + 1,
        }))
        await workOrderService.updateCombinedOrder(technicianId, selectedDate, updates)

        // Update local states to reflect new order
        setWorkOrdersState(prev => {
          const updated = prev.map(wo => {
            const upd = updates.find(u => u.type === 'WorkOrder' && u.id === wo.workOrderID)
            return upd ? { ...wo, techOrderNo: upd.order } : wo
          })
          return updated.sort((a, b) => {
            const aVal = a.techOrderNo ?? null
            const bVal = b.techOrderNo ?? null
            if (aVal === null && bVal === null) return 0
            if (aVal === null) return 1
            if (bVal === null) return -1
            return aVal - bVal
          })
        })

        setDayDestinations(prev => {
          const updated = prev.map(dest => {
            const upd = updates.find(u => u.type === 'Destination' && u.id === dest.technicianDayDestinationID)
            return upd ? { ...dest, orderSequence: upd.order } : dest
          })
          return updated.sort((a, b) => {
            const aVal = a.orderSequence ?? null
            const bVal = b.orderSequence ?? null
            if (aVal === null && bVal === null) return 0
            if (aVal === null) return 1
            if (bVal === null) return -1
            return aVal - bVal
          })
        })

        // Update live ETA after reordering
        try {
          await workOrderService.updateTechnicianETA(technicianId, selectedDate)

          // Refresh work orders and destinations to show updated ETAs
          await refetchWorkOrders()
          const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
          setDayDestinations(destinations)

          notify({ type: 'success', message: 'Order updated! ETAs recalculated.', timestamp: Date.now() })
        } catch (etaErr) {
          const etaMessage = etaErr instanceof Error ? etaErr.message : 'Failed to update ETAs'
          notify({ type: 'warning', message: `Order updated but ${etaMessage}`, timestamp: Date.now() })
        }
      }
    } catch (error) {
      console.error('Error reordering work orders:', error)
      notify({ type: 'error', message: 'Failed to reorder items', timestamp: Date.now() })
    }

    setDraggedItem(null)
    setDraggedOverItem(null)
    setDropPosition(null)
  }

  const handleDragEnd = () => {
    stopAutoScroll()
    if (dragOverHandlerRef.current) {
      document.removeEventListener('dragover', dragOverHandlerRef.current)
      dragOverHandlerRef.current = null
    }
    setDraggedItem(null)
    setDraggedOverItem(null)
    setDropPosition(null)
  }

  // Touch event handlers for iPad and mobile devices
  const [touchStartY, setTouchStartY] = React.useState<number | null>(null)
  const [touchedItem, setTouchedItem] = React.useState<string | null>(null)

  const handleTouchStart = (e: React.TouchEvent, itemKey: string) => {
    const touch = e.touches[0]
    setTouchStartY(touch.clientY)
    setTouchedItem(itemKey)
    setDraggedItem(itemKey)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartY || !touchedItem) return

    e.preventDefault() // Prevent scrolling while dragging

    const touch = e.touches[0]
    const currentY = touch.clientY

    autoScroll(currentY)

    // Find the element under the touch point
    const elementUnder = document.elementFromPoint(touch.clientX, currentY)
    const row = elementUnder?.closest('tr')

    if (row) {
      const itemKey = row.getAttribute('data-item-key')
      if (itemKey && itemKey !== touchedItem) {
        setDraggedOverItem(itemKey)

        // Calculate if touch is in top or bottom half of row
        const rect = row.getBoundingClientRect()
        const midY = rect.top + rect.height / 2
        setDropPosition(currentY < midY ? 'above' : 'below')
      }
    }
  }

  const handleTouchEnd = async (e: React.TouchEvent) => {
    stopAutoScroll()

    if (!touchedItem || !draggedOverItem || touchedItem === draggedOverItem) {
      setTouchStartY(null)
      setTouchedItem(null)
      setDraggedItem(null)
      setDraggedOverItem(null)
      setDropPosition(null)
      return
    }

    // Perform the reorder
    const draggedIndex = combinedItems.findIndex(ci => ci.key === touchedItem)
    const targetIndex = combinedItems.findIndex(ci => ci.key === draggedOverItem)

    if (draggedIndex === -1 || targetIndex === -1) {
      setTouchStartY(null)
      setTouchedItem(null)
      setDraggedItem(null)
      setDraggedOverItem(null)
      return
    }

    const newCombined = [...combinedItems]
    const draggedEntry = newCombined[draggedIndex]
    newCombined.splice(draggedIndex, 1)
    newCombined.splice(targetIndex, 0, draggedEntry)

    try {
      if (isValidTechnician && technicianId && selectedDate) {
        const updates = newCombined.map((ci, index) => ({
          type: ci.kind === 'WorkOrder' ? 'WorkOrder' as const : 'Destination' as const,
          id: ci.kind === 'WorkOrder' ? ci.workOrder.workOrderID : ci.destination.technicianDayDestinationID,
          order: index + 1,
        }))
        await workOrderService.updateCombinedOrder(technicianId, selectedDate, updates)

        setWorkOrdersState(prev => {
          const updated = prev.map(wo => {
            const upd = updates.find(u => u.type === 'WorkOrder' && u.id === wo.workOrderID)
            return upd ? { ...wo, techOrderNo: upd.order } : wo
          })
          return updated.sort((a, b) => {
            const aVal = a.techOrderNo ?? null
            const bVal = b.techOrderNo ?? null
            if (aVal === null && bVal === null) return 0
            if (aVal === null) return 1
            if (bVal === null) return -1
            return aVal - bVal
          })
        })

        setDayDestinations(prev => {
          const updated = prev.map(dest => {
            const upd = updates.find(u => u.type === 'Destination' && u.id === dest.technicianDayDestinationID)
            return upd ? { ...dest, orderSequence: upd.order } : dest
          })
          return updated.sort((a, b) => {
            const aVal = a.orderSequence ?? null
            const bVal = b.orderSequence ?? null
            if (aVal === null && bVal === null) return 0
            if (aVal === null) return 1
            if (bVal === null) return -1
            return aVal - bVal
          })
        })

        // Update live ETA after reordering
        try {
          await workOrderService.updateTechnicianETA(technicianId, selectedDate)

          // Refresh work orders and destinations to show updated ETAs
          await refetchWorkOrders()
          const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
          setDayDestinations(destinations)

          notify({ type: 'success', message: 'Order updated! ETAs recalculated.', timestamp: Date.now() })
        } catch (etaErr) {
          const etaMessage = etaErr instanceof Error ? etaErr.message : 'Failed to update ETAs'
          notify({ type: 'warning', message: `Order updated but ${etaMessage}`, timestamp: Date.now() })
        }
      }
    } catch (error) {
      console.error('Error reordering work orders:', error)
      notify({ type: 'error', message: 'Failed to reorder items', timestamp: Date.now() })
    }

    setTouchStartY(null)
    setTouchedItem(null)
    setDraggedItem(null)
    setDraggedOverItem(null)
    setDropPosition(null)
  }

  const notify = useAppStore(state => state.addNotification)

  const handleOptimizeOrder = async () => {
    if (!isValidTechnician || !technicianId || !selectedDate) return
    try {
      setIsOptimizing(true)
      await workOrderService.optimizeRunningOrder(technicianId, selectedDate)
      notify({ type: 'success', message: 'Running order optimized.', timestamp: Date.now() })
      await refetchWorkOrders()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to optimize running order.'
      notify({ type: 'error', message, timestamp: Date.now() })
    } finally {
      setIsOptimizing(false)
    }
  }

  const handleUpdateEta = async () => {
    if (!isValidTechnician || !technicianId || !selectedDate) return
    try {
      setIsUpdatingEta(true)
      await workOrderService.updateTechnicianETA(technicianId, selectedDate)

      // Fetch fresh data from API and update cache
      await workOrderPrefetchService.prefetchTodayWorkOrders(technicianId)

      // Immediately refresh work orders and destinations from updated cache
      await refetchWorkOrders()
      if (technicianId && selectedDate) {
        const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
        setDayDestinations(destinations)
      }

      notify({ type: 'success', message: 'Live ETA updated.', timestamp: Date.now() })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update ETA.'
      notify({ type: 'error', message, timestamp: Date.now() })
    } finally {
      setIsUpdatingEta(false)
    }
  }

  const handleManualRefresh = async () => {
    if (!isValidTechnician || !technicianId || !selectedDate) return
    try {
      setIsUpdatingEta(true)

      // Fetch fresh data from API and update cache
      await workOrderPrefetchService.prefetchTodayWorkOrders(technicianId)

      // Refresh work orders from updated cache
      await refetchWorkOrders()

      // Refresh destinations from updated cache
      const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
      setDayDestinations(destinations)

      notify({ type: 'success', message: 'Data refreshed.', timestamp: Date.now() })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refresh data.'
      notify({ type: 'error', message, timestamp: Date.now() })
    } finally {
      setIsUpdatingEta(false)
    }
  }

  if (!isValidTechnician) {
    return (
      <div className="container-mobile py-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center">
            <CardTitle className="text-red-600">Invalid Technician</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600">
              Unable to identify technician. Please log out and log in again.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Offline Indicator */}
      {workOrdersListState?.isOffline && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">📱 Offline Mode</span>
            <span className="ml-2">- Showing cached work orders ({workOrdersListState.count} orders)</span>
          </div>
        </div>
      )}

      {/* Controls and Filters */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            id="selectedDate"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-auto"
          />

          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex h-10 w-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="all">All Statuses</option>
            <option value="booked">Booked</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="unable to complete">Unable to Complete</option>
          </select>

          <div className="text-sm text-gray-500">
            {filteredWorkOrders.length} of {workOrders?.length || 0} orders
            {isPrefetching ? (
              <span className="ml-2 text-blue-600">
                <ArrowPathIcon className="inline h-3 w-3 animate-spin mr-1" />
                Caching for offline...
              </span>
            ) : lastPrefetchTime && (
              <span className="ml-2 text-gray-400 text-xs">
                {getTimeUntilNextRefresh()}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowRunningOrderModal(true)}
          >
            Running Order
          </Button> */}
          <Button 
            variant="secondary" 
            size="sm"
            disabled={isUpdatingEta}
            onClick={handleUpdateEta}
          >
            {isUpdatingEta ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
                Updating ETA...
              </>
            ) : (
              'Update ETA'
            )}
          </Button>
          {/* <Button 
            variant="default" 
            size="sm"
            disabled={!isValidTechnician || isOptimizing}
            onClick={handleOptimizeOrder}
          >
            {isOptimizing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
                Optimising...
              </>
            ) : (
              'Optimise Job Ordering'
            )}
          </Button> */}
          
          {/* <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowDayPlanningModal(true)}
          >
            Work Order Scheduling Engine
          </Button> */}
          <Button
            variant={showMap ? "default" : "outline"}
            size="sm"
            onClick={() => setShowMap(!showMap)}
          >
            <MapPinIcon className="h-4 w-4 mr-2" />
            {showMap ? 'Hide Map' : 'Show Map'}
          </Button>
          <Button
            variant={showWorkOrdersOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowWorkOrdersOnly(!showWorkOrdersOnly)}
          >
            {showWorkOrdersOnly ? (
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
                <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
              </svg>
            )}
            Show WO Only
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={isUpdatingEta}
            onClick={handleManualRefresh}
            title="Refresh data"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isUpdatingEta ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Inline Map */}
      {showMap && (
        <WorkOrderMap
          workOrders={workOrdersState.map(wo => ({
            workOrderID: wo.workOrderID,
            jobNo: wo.clientOrderNo,
            pinName: wo.propertyAddress,
            fullAddress: wo.propertyAddress,
            clientName: wo.clientName,
            status: wo.status,
            inspectionType: wo.inspectionType,
            scheduledDate: wo.scheduledDate,
            technicianName: user?.name || '',
            techOrderNo: wo.techOrderNo,
            keyPickup: wo.keyPickup
          }))}
          dayDestinations={dayDestinations}
          technicianId={technicianId || undefined}
          date={selectedDate}
          status={statusFilter !== 'all' ? statusFilter : undefined}
          inline={true}
        />
      )}

      {/* Content */}
      {loadingWorkOrders ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading your work orders...</p>
          </CardContent>
        </Card>
      ) : workOrdersError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-4xl">⚠️</div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-900">Unable to load work orders</h4>
              <p className="text-gray-500 mt-1">
                There was a problem loading your work orders. Please check your connection and try again.
              </p>
              <Button onClick={() => refetchWorkOrders()} className="mt-4">
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : combinedItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-4xl">📝</div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-900">No items found</h4>
              <p className="text-gray-500 mt-1">
                No work orders or destinations match your current filters. Try adjusting your search criteria or date range.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Table View - All Screen Sizes */}
          <div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-4 font-medium w-16"></th>
                        <th className="text-left p-4 font-medium w-32">Tech Order</th>
                        <th className="text-left p-4 font-medium">Client</th>
                        <th className="text-left p-4 font-medium">Details</th>
                        <th className="text-left p-4 font-medium w-32 md:w-48 lg:w-auto">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinedItems.map((item) => {
                        const isDraggedOver = draggedOverItem === item.key
                        if (item.kind === 'WorkOrder') {
                          const wo = item.workOrder
                          const workOrderId = wo.workOrderID
                          const clientName = wo.clientName || 'Unknown Client'
                          const propertyAddress = wo.propertyAddress || 'No Address'
                          const packageName = wo.packageDesc || wo.packageDetails
                          const inspectionType = wo.inspectionType || 'General Inspection'
                          const keyNumber = wo.keyNo
                          const hasClientNotes = wo.clientNotes && wo.clientNotes.trim() !== ''
                          const isBeingDragged = draggedItem === item.key
                          if (!workOrderId) return null
                          return (
                            <tr
                              key={item.key}
                              data-item-key={item.key}
                              className={`border-b transition-all duration-200 ${
                                !wo.keyPickup ? 'bg-gray-100 hover:bg-gray-150' : 'hover:bg-gray-50'
                              } ${
                                isDraggedOver ? 'bg-blue-50 border-blue-200' : ''
                              } ${
                                isBeingDragged ? 'opacity-50 scale-95' : ''
                              }`}
                              style={{
                                borderLeft: '4px solid #007bff',
                                borderTop: isDraggedOver && dropPosition === 'above' ? '4px solid #3b82f6' : undefined,
                                borderBottom: isDraggedOver && dropPosition === 'below' ? '4px solid #3b82f6' : undefined,
                                boxShadow: isBeingDragged ? '0 8px 16px rgba(0, 0, 0, 0.15)' : 'none',
                                transform: isBeingDragged ? 'scale(0.98)' : 'scale(1)'
                              }}
                              draggable
                              onDragStart={(e) => handleDragStart(e, item.key)}
                              onDragOver={(e) => handleDragOver(e, item.key)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, item.key)}
                              onDragEnd={handleDragEnd}
                            >
                              <td className="p-2">
                                <div
                                  className="flex items-center justify-center cursor-move p-3 -m-2"
                                  style={{ touchAction: 'none', minWidth: '44px', minHeight: '44px' }}
                                  onTouchStart={(e) => handleTouchStart(e, item.key)}
                                  onTouchMove={handleTouchMove}
                                  onTouchEnd={handleTouchEnd}
                                >
                                  <Bars3Icon className={`h-6 w-6 transition-colors ${
                                    isBeingDragged ? 'text-blue-600' : 'text-gray-400'
                                  }`} />
                                </div>
                              </td>
                              <td className="p-4">
                                {wo.editingTechOrderNo ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="number"
                                      value={wo.tempTechOrderNo || ''}
                                      onChange={(e) => updateTempTechOrderNo(workOrderId, e.target.value)}
                                      className="w-20 text-sm"
                                      placeholder="Order"
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => saveTechOrderNo(workOrderId, wo.tempTechOrderNo ?? null)}
                                    >
                                      <CheckIcon className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => cancelEditingTechOrderNo(workOrderId)}
                                    >
                                      <XMarkIcon className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{wo.techOrderNo || '-'}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => startEditingTechOrderNo(workOrderId)}
                                      >
                                        <PencilIcon className="h-4 w-4 text-gray-400" />
                                      </Button>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {wo.editingRequestedTime ? (
                                        <div className="flex items-center gap-1 mb-1">
                                          <Input
                                            type="time"
                                            value={wo.tempRequestedTime || ''}
                                            onChange={(e) => updateTempRequestedTime(workOrderId, e.target.value)}
                                            className="h-6 text-xs min-w-32"
                                          />
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => saveRequestedTime(workOrderId, wo.tempRequestedTime || null)}
                                          >
                                            <CheckIcon className="h-3 w-3 text-green-600" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => cancelEditingRequestedTime(workOrderId)}
                                          >
                                            <XMarkIcon className="h-3 w-3 text-red-600" />
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1 mb-1">
                                          {wo.requestedTime ? (
                                            <>
                                              <span>REQ: {formatTime(wo.requestedTime)}</span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-4 w-4 p-0"
                                                onClick={() => startEditingRequestedTime(workOrderId)}
                                              >
                                                <PencilIcon className="h-3 w-3 text-gray-400" />
                                              </Button>
                                            </>
                                          ) : (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-5 px-1 text-xs"
                                              onClick={() => startEditingRequestedTime(workOrderId)}
                                            >
                                              + Add REQ
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                      {(wo.eta || wo.scheduledDate) && (
                                        <div>ETA: {formatTime(wo.eta || wo.scheduledDate)}</div>
                                      )}
                                    </div>
                                    <div className="mt-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleWorkOrderClick(workOrderId)}
                                      >
                                        View Details
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="p-4">
                                <div className="flex flex-col gap-2">
                                  <span className="text-sm font-bold line-clamp-3">{propertyAddress.replace(/\s*\d{4}\s*$/, '')}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 line-clamp-3">{clientName}</span>
                                    {hasClientNotes && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="p-0 h-auto"
                                        onClick={() => showClientNotesDialog(wo.clientNotes || '')}
                                      >
                                        <StarIconSolid className="h-4 w-4 text-yellow-500" />
                                      </Button>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-auto p-0 text-blue-600 hover:text-blue-800 text-xs self-start"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openGoogleMaps(propertyAddress)
                                    }}
                                  >
                                    📍 Open in Maps
                                  </Button>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-600">
                                    {inspectionType} <br />
                                    {packageName} <br />
                                    Key: {keyNumber}
                                  </span>
                                </div>
                              </td>
                              <td className="p-4">
                                {wo.editingComments ? (
                                  <div className="flex items-center gap-2">
                                    <Textarea
                                      value={wo.tempComments || ''}
                                      onChange={(e) => updateTempComments(workOrderId, e.target.value)}
                                      className="min-h-[60px] text-sm w-full"
                                      placeholder="Add comments..."
                                    />
                                    <div className="flex flex-col gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => saveComments(workOrderId, wo.tempComments || '')}
                                      >
                                        <CheckIcon className="h-4 w-4 text-green-600" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => cancelEditingComments(workOrderId)}
                                      >
                                        <XMarkIcon className="h-4 w-4 text-red-600" />
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 break-words">
                                      {wo.workOrderNotes || 'No comments'}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => startEditingComments(workOrderId)}
                                    >
                                      <PencilIcon className="h-4 w-4 text-gray-400" />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )
                        }
                        // Destination row
                        const dest = item.destination
                        const name = dest.destinationName
                        const address = dest.address
                        const type = dest.destinationType
                        const notes = dest.notes
                        const isBeingDraggedDest = draggedItem === item.key
                        return (
                          <tr
                            key={item.key}
                            data-item-key={item.key}
                            className={`border-b bg-gray-100 hover:bg-gray-150 transition-all duration-200 ${
                              isDraggedOver ? 'bg-blue-50 border-blue-200' : ''
                            } ${
                              isBeingDraggedDest ? 'opacity-50 scale-95' : ''
                            }`}
                            style={{
                              borderLeft: '4px solid #28a745',
                              borderTop: isDraggedOver && dropPosition === 'above' ? '4px solid #3b82f6' : undefined,
                              borderBottom: isDraggedOver && dropPosition === 'below' ? '4px solid #3b82f6' : undefined,
                              boxShadow: isBeingDraggedDest ? '0 8px 16px rgba(0, 0, 0, 0.15)' : 'none',
                              transform: isBeingDraggedDest ? 'scale(0.98)' : 'scale(1)'
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, item.key)}
                            onDragOver={(e) => handleDragOver(e, item.key)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, item.key)}
                            onDragEnd={handleDragEnd}
                          >
                            <td className="p-2">
                              <div
                                className="flex items-center justify-center cursor-move p-3 -m-2"
                                style={{ touchAction: 'none', minWidth: '44px', minHeight: '44px' }}
                                onTouchStart={(e) => handleTouchStart(e, item.key)}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                              >
                                <Bars3Icon className={`h-6 w-6 transition-colors ${
                                  isBeingDraggedDest ? 'text-green-600' : 'text-gray-400'
                                }`} />
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{item.order ?? '-'}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  {dest.scheduledTime && (
                                    <div>ETA: {formatTime(dest.scheduledTime)}</div>
                                  )}
                                </div>
                                <div className="mt-2">
                                  <Button
                                    variant={dest.completed ? 'secondary' : 'outline'}
                                    size="sm"
                                    onClick={async (e) => {
                                      e.stopPropagation()
                                      try {
                                        const newCompletedStatus = !dest.completed
                                        await workOrderService.setDestinationCompleted(dest.technicianDayDestinationID, newCompletedStatus)
                                        setDayDestinations(prev => prev.map(d => d.technicianDayDestinationID === dest.technicianDayDestinationID ? { ...d, completed: newCompletedStatus } : d))

                                        // If marking a KeyPickup as completed, update live ETA
                                        if (newCompletedStatus && dest.destinationType === 'KeyPickup' && isValidTechnician && technicianId && selectedDate) {
                                          try {
                                            await workOrderService.updateTechnicianETA(technicianId, selectedDate)

                                            // Refresh work orders and destinations
                                            await refetchWorkOrders()
                                            const destinations = await offlineWorkOrdersListService.loadDayDestinations(technicianId, selectedDate)
                                            setDayDestinations(destinations)

                                            notify({ type: 'success', message: 'Key pickup complete! ETAs updated.', timestamp: Date.now() })
                                          } catch (etaErr) {
                                            const etaMessage = etaErr instanceof Error ? etaErr.message : 'Failed to update ETAs'
                                            notify({ type: 'warning', message: `Destination completed but ${etaMessage}`, timestamp: Date.now() })
                                          }
                                        }
                                      } catch (err) {
                                        console.error('Error updating destination completed:', err)
                                        notify({ type: 'error', message: 'Failed to update destination status', timestamp: Date.now() })
                                      }
                                    }}
                                  >
                                    {dest.completed ? 'Completed' : 'Mark Complete'}
                                  </Button>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium line-clamp-3">{name}</span>
                                <span className="text-sm text-gray-600 line-clamp-3">{address.replace(/\s*\d{4}\s*$/, '')}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-auto p-0 text-blue-600 hover:text-blue-800 text-xs self-start"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    openGoogleMaps(address)
                                  }}
                                >
                                  📍 Open in Maps
                                </Button>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                  {type}
                                </span>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 break-words">
                                  {notes || '—'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Client Notes Modal */}
      <Dialog open={showClientNotesModal} onOpenChange={setShowClientNotesModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Client Notes</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedClientNotes}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Running Order Modal */}
      <RunningOrderModal
        open={showRunningOrderModal}
        onOpenChange={setShowRunningOrderModal}
        defaultTechnicianId={technicianId || undefined}
        defaultDate={selectedDate}
      />

      {/* Day Planning Modal */}
      <DayPlanningModal
        open={showDayPlanningModal}
        onOpenChange={setShowDayPlanningModal}
      />
    </div>
  )
}

export default WorkOrdersList 